import React, { forwardRef } from 'react';
import Receipt from './Receipt.js';
import Token from './Token.js';
import useAppStore from '../../store/useAppStore.js';

/**
 * Unified PrintContent component using forwardRef
 * Conditionally renders Receipt and/or Token based on printMode
 * 
 * @param {Object} props
 * @param {string} props.printMode - 'receipt', 'token', or 'both'
 * @param {Object} props.order - Normalized order data
 * @param {Array} props.order.items - Order items with id, name, price, quantity
 * @param {number} props.order.subtotal
 * @param {number} props.order.discount
 * @param {number} props.order.total
 * @param {Object} props.order.table - Table info (optional)
 * @param {Object} props.order.token - Token info (optional)
 * @param {string} ref - Forwarded ref for react-to-print
 */
const PrintContent = forwardRef(({ printMode = 'receipt', order = {} }, ref) => {
  const { selectedBranch } = useAppStore((state) => ({
    selectedBranch: state.selectedBranch
  }));
  // Return null if no print mode or order
  if (!printMode || !order) {
    return <div ref={ref} />;
  }

  // Normalize order data
  const normalizedOrder = {
    id: order.id || order.orderId || null,
    order_number: order.order_number || null,
    items: order.items || [],
    subtotal: order.subtotal || 0,
    discount: order.discount || 0,
    total: order.total || 0,
    table: order.table || null,
    token: order.token || null,
    receipt: order.receipt || null,
    order_type: order.order_type || order.orderType || null,
    createdAt: order.createdAt || new Date().toLocaleString()
  };
  
  const tokenGroups = order.tokenGroups || [];

  return (
    <div
      ref={ref}
      style={{
        width: '80mm',
        maxWidth: '100%',
        backgroundColor: 'white',
        color: '#000'
      }}
    >
      {/* Render Receipt if printMode is 'receipt' or 'both' */}
      {(printMode === 'receipt' || printMode === 'both') && (
        <div style={{ pageBreakAfter: printMode === 'both' ? 'always' : 'auto' }}>
          <Receipt order={normalizedOrder} branch={selectedBranch} />
        </div>
      )}

      {/* Render Token(s) if printMode is 'token' or 'both' */}
      {(printMode === 'token' || printMode === 'both') && (
        <>
          {tokenGroups.length > 0 ? (
            tokenGroups.map((group, index) => (
              <div key={`${group.token_no || group.id || index}-${group.paper}`} style={{ pageBreakBefore: index === 0 ? 'auto' : 'always' }}>
                <Token
                  order={{
                    ...normalizedOrder,
                    items: group.items,
                    token: group,
                    paper: group.paper
                  }}
                  branch={selectedBranch}
                />
              </div>
            ))
          ) : (
            <Token order={normalizedOrder} branch={selectedBranch} />
          )}
        </>
      )}
    </div>
  );
});

PrintContent.displayName = 'PrintContent';

export default PrintContent;
